import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import { getTimezoneNames } from "../utils/timezones";
import { handleDeleteError, handleUpdateError, handleCreateError, handleToggleError } from "../utils/errorHandler";
import {
  Box,
  Container,
  Tooltip,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Popover,
  Menu,
  MenuItem,
  Switch,
  Autocomplete,
  FormControl,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function Schedule_details() {
  const [formData, setFormData] = useState({
    cl_id: "",
    client_name: "",
    env_id: "",
    app_id: "",
    job_id: "",
    time_zone: "",
    prev_start_scheduled_time: "",
    start_scheduled_time: "",
    end_scheduled_time: "",
    interval_mins: "",
    rh_eom: "",
    rh_prev_eom: "",
    reference_1: "",
  });
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  
  // Get timezones from centralized utility
  const timezones = getTimezoneNames();
  
  const [clients, setClients] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [originalStartTime, setOriginalStartTime] = useState("");
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    environment_name: true,
    application_name: true,
    jobs_script: true,
    time_zone: true,
    start_scheduled_time: true,
    end_scheduled_time: true,
    interval_mins: true,
    rh_eom: true,
    rh_prev_eom: true,
    reference_1: true,
  });
  const [filters, setFilters] = useState({
    client_name: "",
    environment_name: "",
    application_name: "",
    jobs_script: "",
    time_zone: "",
    start_scheduled_time: "",
    end_scheduled_time: "",
    interval_mins: "",
    rh_eom: "",
    rh_prev_eom: "",
    reference_1: "",
  });
  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    jobs_script: "Job",
    time_zone: "Time Zone",
    start_scheduled_time: "Start Time",
    end_scheduled_time: "End Time",
    interval_mins: "Interval Time",
    rh_eom: "RH EOM",
    rh_prev_eom: "RH Pre EOM",
    reference_1: "Frequency",
  };
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      prev_start_scheduled_time: prev.start_scheduled_time || "",
      [name]: value,
    }));
  };

  // Sync selectedDays with formData.reference_1
  useEffect(() => {
    if (formData.reference_1) {
      const days = formData.reference_1.split(',').filter(d => d.trim());
      setSelectedDays(days);
    } else {
      setSelectedDays([]);
    }
  }, [formData.reference_1]);

  // Update formData.reference_1 when selectedDays changes
  useEffect(() => {
    const frequencyString = selectedDays.join(',');
    setFormData(prev => ({
      ...prev,
      reference_1: frequencyString
    }));
  }, [selectedDays]);

  const handleEnvironmentChange = (event, newValue) => {
    const envId = newValue ? newValue.env_id : "";
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Reset application when environment changes
      job_id: "", // Reset job when environment changes
    }));
    setFormErrors((prev) => ({ ...prev, env_id: "" }));

    // Fetch applications for the selected environment
    if (envId) {
      fetchApplications(envId);
    } else {
      setApplications([]);
    }

    // Clear dependent dropdowns
    setJobs([]);
  };

  const handleApplicationChange = (event, newValue) => {
    const appId = newValue ? newValue.app_id : "";
    setFormData((prev) => ({
      ...prev,
      app_id: appId,
      job_id: "", // Reset job when application changes
    }));
    setFormErrors((prev) => ({ ...prev, app_id: "" }));

    // Fetch jobs for the selected environment and application
    if (formData.env_id && appId) {
      fetchJobs(formData.env_id, appId);
    } else {
      setJobs([]);
    }
  };

  const validate = () => {
    const errors = {};

    // Client is pre-selected, no need to validate
    if (!formData.env_id) {
      errors.env_id = "Environment is required.";
    }
    if (!formData.app_id) {
      errors.app_id = "Application is required.";
    }
    if (!formData.job_id) {
      errors.job_id = "Job ID is required.";
    }
    if (!formData.time_zone) {
      errors.time_zone = "Time Zone is required.";
    }
    if (!formData.start_scheduled_time) {
      errors.start_scheduled_time = "Start time is required.";
    }
    if (!formData.end_scheduled_time) {
      errors.end_scheduled_time = "End time is required.";
    }
    if (formData.rh_eom === null || formData.rh_eom === undefined || formData.rh_eom === "") {
      errors.rh_eom = "Running Hrs EOM is required.";
    }
    if (formData.rh_prev_eom === null || formData.rh_prev_eom === undefined || formData.rh_prev_eom === "") {
      errors.rh_prev_eom = "Running Hrs Previous EOM is required.";
    }

    return errors;
  };

  const fetchClients = async () => {
    try {
      const data = await apiService.get("/schedule/clients");
      setClients(data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/schedule/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error fetching environments:", err);
    }
  };

  const fetchApplications = async (envId = null) => {
    try {
      let url = "/schedule/applications";
      if (envId) {
        url += `?env_id=${envId}`;
      }
      const data = await apiService.get(url);
      setApplications(data);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setApplications([]);
    }
  };

  const fetchJobs = async (envId = null, appId = null) => {
    try {
      let url = "/schedule/jobs";
      const params = new URLSearchParams();
      if (envId) params.append("env_id", envId);
      if (appId) params.append("app_id", appId);
      if (params.toString()) url += `?${params.toString()}`;

      const data = await apiService.get(url);
      setJobs(data);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setJobs([]);
    }
  };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);

    const params = new URLSearchParams({
      page,
      limit: rowsPerPage,
    });

    // ✅ Correct filter handling
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        value.toString().trim() !== ""
      ) {
        params.append(key, value.toString().trim());
      }
    });

    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm.trim());
    }

    try {
      const result = await apiService.get(
        `/schedule/paginate/list?${params.toString()}`,
      );

      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, searchTerm]);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients, formData.cl_id]);

  useEffect(() => {
    fetchClients();
    fetchApplications(); // Load all applications initially
    fetchEnvironments();
    fetchJobs(); // Load all jobs initially
    fetchSchedules();
  }, [fetchSchedules]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (isEditing) {
        await apiService.put(`/schedule/${formData.job_id}`, formData);
      } else {
        await apiService.post("/schedule", formData);
      }

      setSuccessMessage(
        isEditing
          ? "Schedule updated successfully!"
          : "Schedule created successfully!",
      );
      setFormData({
        cl_id: clients.length > 0 ? clients[0].cl_id : "",
        client_name: clients.length > 0 ? clients[0].name : "",
        env_id: "",
        app_id: "",
        job_id: "",
        time_zone: "",
        start_scheduled_time: "",
        end_scheduled_time: "",
        interval_mins: "",
        rh_eom: "",
        rh_prev_eom: "",
      });
      setIsEditing(false);
      fetchSchedules();
      setTimeout(() => setSuccessMessage(""), 3000);
      setOpenDialog(false);
    } catch (error) {
      console.error("Error saving schedule:", error);
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(error, "Schedule")
        : handleCreateError(error, "Schedule");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleEdit = async (schedule) => {
    setOriginalStartTime(schedule.start_scheduled_time);
    setIsEditing(true);
    setFormErrors({});

    // Load dependent data for editing FIRST
    if (schedule.env_id) {
      await fetchApplications(schedule.env_id);
      if (schedule.app_id) {
        await fetchJobs(schedule.env_id, schedule.app_id);
      }
    }

    // Small delay to ensure dependent data is fully loaded and UI is updated
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Prepare form data
    const editFormData = {
      ...schedule,
      prev_start_scheduled_time: schedule.start_scheduled_time,
      reference_1: schedule.reference_1 || "",
    };

    // Set form data AFTER dependent data is loaded
    setFormData(editFormData);
    setOriginalFormData({ ...editFormData }); // Store original data for comparison
    setIsFormChanged(false); // Reset change flag

    setOpenDialog(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteRow(row);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;

    try {
      await apiService.delete(
        `/schedule/${deleteRow.start_scheduled_time}?cl_id=${deleteRow.cl_id}&env_id=${deleteRow.env_id}&app_id=${deleteRow.app_id}&job_id=${deleteRow.job_id}`
      );

      setSuccessMessage("Schedule deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchSchedules();
      setOpenDeleteDialog(false);
      setDeleteRow(null);
    } catch (err) {
      setOpenDeleteDialog(false);
      setDeleteRow(null);
      
      // Use centralized error handler
      const errorMessage = handleDeleteError(err, "Schedule");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteRow(null);
  };

  const handleDialogOpen = () => {
    setFormData({
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      app_id: "",
      job_id: "",
      time_zone: "",
      start_scheduled_time: "",
      end_scheduled_time: "",
      interval_mins: "",
      rh_eom: "",
      rh_prev_eom: "",
      reference_1: "",
    });
    setSelectedDays([]);
    setFormErrors({});
    setIsEditing(false);

    // Clear dependent dropdowns when opening new form
    setApplications([]);
    setJobs([]);

    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setFormData({
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      app_id: "",
      job_id: "",
      time_zone: "",
      start_scheduled_time: "",
      end_scheduled_time: "",
      interval_mins: "",
      rh_eom: "",
      rh_prev_eom: "",
      reference_1: "",
    });
    setSelectedDays([]);
    setFormErrors({});
    setIsEditing(false);
    setOriginalFormData({});
    setIsFormChanged(false);
    setApplications([]);
    setJobs([]);
    setOpenDialog(false);
  };

  const whiteBlackFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#686970", border: "1px solid #444444" },
      "&:hover fieldset": { borderColor: "#686970" },
      "&.Mui-focused fieldset": { borderColor: "#686970" },
    },
    "& .MuiInputLabel-root": { color: "#686970" },
  };

  const columns = [
    "client_name",
    "environment_name",
    "application_name",
    "jobs_script",
    "time_zone",
    "start_scheduled_time",
    "end_scheduled_time",
    "interval_mins",
    "rh_eom",
    "rh_prev_eom",
    "reference_1",
  ];

  const daysOfWeek = [
    { label: "Mon", value: "Mon" },
    { label: "Tue", value: "Tue" },
    { label: "Wed", value: "Wed" },
    { label: "Thu", value: "Thu" },
    { label: "Fri", value: "Fri" },
    { label: "Sat", value: "Sat" },
    { label: "Sun", value: "Sun" },
  ];

  const handleToggleFlag = async (schedule, active_flag) => {
    try {
      const newFlag = active_flag === 1 ? 0 : 1;
      await apiService.put(`/schedule/${schedule.job_id}`, {
        active_flag: newFlag,
        cl_id: schedule.cl_id,
        env_id: schedule.env_id,
        app_id: schedule.app_id,
        prev_start_scheduled_time: schedule.start_scheduled_time,
      });
      
      fetchSchedules();
      
      // Show success message
      const message = newFlag === 1 
        ? "Schedule Activated" 
        : "Schedule Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      // Use centralized error handler
      const errorMessage = handleToggleError(error, "Schedule");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Schedules Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Schedules
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={handleDialogOpen}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>

              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2DB0E4",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>
      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  type={
                                    col === "rh_eom" || col === "rh_prev_eom"
                                      ? "number"
                                      : "text"
                                  }
                                  value={tempFilters[col] || ""}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                  size="small"
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1}
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((s) => (
                      <TableRow
                        key={`${s.job_id}-${s.start_scheduled_time}-${s.end_scheduled_time}`}
                      >
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(s, parseInt(s.active_flag))
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(s.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(s)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(s)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {columns.map(
                          (col) =>
                            visibleColumns[col] && (
                              <TableCell key={col}>{s[col]}</TableCell>
                            ),
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1}
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>

        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            // onClose={handleDialogClose}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDialog(false);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: "700",
                fontSize: "16px",
                py: 1,
              }}
            >
              {isEditing ? "Edit Schedule" : "Add Schedule"}
              <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2.1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                {[
                  {
                    label: "Client",
                    name: "cl_id",
                    type: "readonly",
                  },
                  {
                    label: "Environment",
                    name: "env_id",
                    type: "select",
                    options: environments,
                    optionKey: "env_id",
                    optionLabel: "name",
                    onChange: handleEnvironmentChange,
                  },
                  {
                    label: "Application",
                    name: "app_id",
                    type: "select",
                    options: applications,
                    optionKey: "app_id",
                    optionLabel: "name",
                    onChange: handleApplicationChange,
                    disabled: !formData.env_id,
                    helperText: !formData.env_id,
                  },
                  {
                    label: "Job",
                    name: "job_id",
                    type: "select",
                    readonly: true,
                    options: jobs,
                    optionKey: "job_id",
                    optionLabel: "jobs_script",
                    formatOption: (job) => `${job.jobs_script}`,
                    disabled: !formData.app_id || isEditing,
                    helperText: !formData.app_id,
                  },
                  { 
                    label: "Time Zone", 
                    name: "time_zone",
                    type: "timezone"
                  },
                  {
                    label: "Start Time",
                    name: "start_scheduled_time",
                    type: "time",
                  },
                  {
                    label: "End Time",
                    name: "end_scheduled_time",
                    type: "time",
                  },
                  {
                    label: "Interval (min)",
                    name: "interval_mins",
                    type: "number",
                  },
                   {
                    label: "Frequency",
                    name: "days_of_week",
                    type: "daysofweek",
                  },
                  { label: "RH EOM", name: "rh_eom", type: "number", min: 0 },
                 
                  { label: "Prev EOM", name: "rh_prev_eom", type: "number", min: 0 },
                ].map((field) =>
                  field.type === "readonly" ? (
                    <TextField
                      key={field.name}
                      label={field.label}
                      value={
                        formData.client_name ||
                        clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                        ""
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        backgroundColor: "white",
                        "& .MuiInputBase-root": {
                          height: 35,
                        },
                        "& .MuiInputBase-input": {
                          cursor: "not-allowed",
                          color: "grey",
                          fontSize: "0.75rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.75rem",
                        },
                      }}
                      fullWidth
                      margin="normal"
                    />
                  ) : field.type === "timezone" ? (
                    <FormControl
                      key={field.name}
                      fullWidth
                      margin="none"
                      error={!!formErrors[field.name]}
                    >
                      <Autocomplete
                        id={field.name}
                        options={timezones}
                        value={formData[field.name] || null}
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.name]: newValue || "",
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            [field.name]: "",
                          }));
                        }}
                        ListboxProps={{
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "0.75rem",
                              minHeight: "32px",
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={field.label}
                            required
                            error={!!formErrors[field.name]}
                            helperText={formErrors[field.name]}
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 35,
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "0.75rem",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  ) : field.type === "select" ? (
                    <FormControl
                      key={field.name}
                      fullWidth
                      margin="none"
                      error={!!formErrors[field.name]}
                    >
                      <Autocomplete
                        id={field.name}
                        options={field.options}
                        getOptionLabel={(option) =>
                          field.formatOption
                            ? field.formatOption(option)
                            : option[field.optionLabel] || ""
                        }
                        value={
                          field.options.find(
                            (option) =>
                              option[field.optionKey] === formData[field.name],
                          ) || null
                        }
                        onChange={
                          field.onChange ||
                          ((event, newValue) => {
                            setFormData((prev) => ({
                              ...prev,
                              [field.name]: newValue
                                ? newValue[field.optionKey]
                                : "",
                            }));
                            setFormErrors((prev) => ({
                              ...prev,
                              [field.name]: "",
                            }));
                          })
                        }
                        ListboxProps={{
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "0.875rem",
                              minHeight: "32px",
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={field.label}
                            required={
                              field.name === "env_id" ||
                              field.name === "app_id" ||
                              field.name === "job_id"
                            }
                            error={!!formErrors[field.name]}
                            helperText={
                              formErrors[field.name] || field.helperText
                            }
                            sx={{
                              ...(field.name === "job_id" && isEditing
                                ? {
                                    "& .MuiInputBase-input": {
                                      cursor: "not-allowed",
                                      color: "grey",
                                      backgroundColor: "white",
                                      fontSize: "0.75rem",
                                    },
                                  }
                                : {}),
                              "& .MuiInputBase-root": {
                                height: 35,
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "0.75rem",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "0.75rem",
                              },
                            }}
                            InputProps={{
                              ...params.InputProps,
                              readOnly: field.name === "job_id" && isEditing,
                            }}
                          />
                        )}
                        disabled={field.disabled || field.options.length === 0}
                        noOptionsText={
                          field.helperText || "No options available"
                        }
                      />
                    </FormControl>
                  ) : field.type === "daysofweek" ? (
                    <FormControl
                      key={field.name}
                      fullWidth
                      margin="none"
                    >
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={daysOfWeek}
                        getOptionLabel={(option) => option.label === "Mon" ? "Monday" : 
                                                   option.label === "Tue" ? "Tuesday" :
                                                   option.label === "Wed" ? "Wednesday" :
                                                   option.label === "Thu" ? "Thursday" :
                                                   option.label === "Fri" ? "Friday" :
                                                   option.label === "Sat" ? "Saturday" : "Sunday"}
                        value={daysOfWeek.filter(day => selectedDays.includes(day.value))}
                        onChange={(event, newValue) => {
                          setSelectedDays(newValue.map(day => day.value));
                        }}
                        ListboxProps={{
                          style: {
                            maxHeight: "250px",
                          },
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "0.75rem",
                              minHeight: "32px",
                            },
                          },
                        }}
                        slotProps={{
                          popper: {
                            placement: "bottom-start",
                            modifiers: [
                              {
                                name: "flip",
                                enabled: false,
                              },
                            ],
                          },
                        }}
                        renderOption={(props, option, { selected }) => {
                          const allSelected = selectedDays.length === daysOfWeek.length;
                          
                          return (
                            <>
                              {option.value === "Mon" && (
                                <li
                                  key="select-all"
                                  onClick={() => {
                                    if (allSelected) {
                                      setSelectedDays([]);
                                    } else {
                                      setSelectedDays(daysOfWeek.map(d => d.value));
                                    }
                                  }}
                                  style={{
                                    padding: "6px 16px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #e0e0e0",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  <Checkbox
                                    checked={allSelected}
                                    indeterminate={selectedDays.length > 0 && selectedDays.length < daysOfWeek.length}
                                    sx={{ marginRight: 1 }}
                                  />
                                  Select All
                                </li>
                              )}
                              <li {...props}>
                                <Checkbox
                                  checked={selected}
                                  sx={{ marginRight: 1 }}
                                />
                                {option.label === "Mon" ? "Monday" : 
                                 option.label === "Tue" ? "Tuesday" :
                                 option.label === "Wed" ? "Wednesday" :
                                 option.label === "Thu" ? "Thursday" :
                                 option.label === "Fri" ? "Friday" :
                                 option.label === "Sat" ? "Saturday" : "Sunday"}
                              </li>
                            </>
                          );
                        }}
                        renderTags={(value) => (
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                              width: "100%",
                            }}
                          >
                            {value.map(option => option.value).join(", ")}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={field.label}
                            
                            sx={{
                              ...whiteBlackFieldStyle,
                              "& .MuiInputBase-root": {
                                height: 35,
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "0.75rem",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "0.75rem",
                              },
                              "& .MuiAutocomplete-input": {
                                minWidth: "30px !important",
                              },
                            }}
                          />
                        )}
                        ListboxProps={{
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "0.75rem",
                              minHeight: "32px",
                            },
                          },
                        }}
                      />
                    </FormControl>
                  ) : (
                    <TextField
                      key={field.name}
                      label={field.label}
                      name={field.name}
                      type={field.type || "text"}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        // Prevent 'e', 'E', '+', '-' in number fields
                        if (field.type === "number" && ['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      fullWidth
                      required={!field.readonly}
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={!!formErrors[field.name]}
                      helperText={formErrors[field.name] || field.helperText}
                      inputProps={field.type === "number" && field.min !== undefined ? { min: field.min } : {}}
                      sx={{
                        ...whiteBlackFieldStyle,
                        "& .MuiInputBase-root": {
                          height: 35,
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "0.75rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.75rem",
                        },
                        ...(field.readonly && {
                          "& .MuiInputBase-input": {
                            cursor: "not-allowed",
                            color: "grey",
                            backgroundColor: "white",
                            fontSize: "0.75rem",
                          },
                        }),
                      }}
                      InputProps={{
                        readOnly: field.readonly,
                      }}
                      margin="normal"
                    />
                  ),
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained"
                disabled={isEditing && !isFormChanged}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              cancelDelete();
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold", fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Schedule for Job{" "}
                <strong>
                  {deleteRow?.jobs_script || deleteRow?.job_id}
                </strong>{" "}
                at <strong>{deleteRow?.start_scheduled_time}</strong> ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button onClick={cancelDelete}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={confirmDelete}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
}

export default Schedule_details;
